import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useCreateUpsell } from "@/hooks/use-upsells";
import type { DBVoucher } from "@/lib/db-types";
import { formatNOK } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";

interface VoucherToUpsellDialogProps {
  voucher: DBVoucher | null;
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function defaultDescription(voucher: DBVoucher) {
  const supplier = voucher.supplier_name?.trim();
  const desc = voucher.description?.trim();
  if (supplier && desc) return `Mersalg: ${supplier} – ${desc}`;
  if (supplier) return `Mersalg: ${supplier}`;
  if (desc) return `Mersalg: ${desc}`;
  return `Mersalg fra bilag #${voucher.voucher_no ?? ""}`;
}

export default function VoucherToUpsellDialog({
  voucher,
  projectId,
  open,
  onOpenChange,
}: VoucherToUpsellDialogProps) {
  const createMut = useCreateUpsell();
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState<"bilag" | "reise">("bilag");

  useEffect(() => {
    if (open && voucher) {
      setDescription(defaultDescription(voucher));
      setAmount(voucher.amount != null ? String(voucher.amount) : "");
      setCategory("bilag");
    }
  }, [open, voucher]);

  if (!voucher) return null;

  const parsedAmount = parseFloat(amount);
  const amountValid = !isNaN(parsedAmount) && parsedAmount > 0;
  const canSubmit = description.trim().length > 0 && amountValid && !createMut.isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await createMut.mutateAsync({
        project_id: projectId,
        description: description.trim(),
        amount_excl_vat: parsedAmount,
        category,
        source_voucher_id: voucher.id,
      });
      onOpenChange(false);
    } catch {
      // Error toast handled inside hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-lg">Viderefakturer som mersalg</DialogTitle>
          <DialogDescription>
            Oppretter en mersalgslinje basert på dette bilaget. Beløp og beskrivelse kan justeres.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground space-y-0.5">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-foreground">
              {voucher.supplier_name ?? `Bilag #${voucher.voucher_no ?? ""}`}
            </span>
            <span className="tabular-nums">{formatNOK(voucher.amount)}</span>
          </div>
          {voucher.description && <p className="truncate">{voucher.description}</p>}
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Beskrivelse
            </label>
            <input
              type="text"
              className="mt-1 w-full h-9 rounded-md border-[1.5px] border-foreground bg-background px-2 text-sm"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Beløp eks. mva
            </label>
            <input
              type="number"
              step="0.01"
              className="mt-1 w-full h-9 rounded-md border-[1.5px] border-foreground bg-background px-2 text-sm tabular-nums"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Type
            </label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {([
                { value: "bilag", label: "Bilag" },
                { value: "reise", label: "Reisekostnad" },
              ] as const).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setCategory(opt.value)}
                  className={cn(
                    "h-9 rounded-md border-[1.5px] text-sm font-medium transition-colors",
                    category === opt.value
                      ? "border-foreground bg-foreground text-background"
                      : "border-border bg-background text-foreground hover:bg-muted/50",
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={createMut.isPending}>
            Avbryt
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {createMut.isPending ? (
              <SrIcon name="loader2" className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <SrIcon name="plus" className="w-4 h-4 mr-1.5" />
            )}
            Opprett mersalg
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
